import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getTenantSubdomain } from '@/utils/tenantDetection';
import type { TenantBrandingRow, ProcessedBranding } from '@/types/tenant';

/**
 * Valores padrão de branding (sem tenant customizado)
 */
const DEFAULT_BRANDING: ProcessedBranding = {
  logoUrl: '', // Sem logo customizada (não mostra no card)
  backgroundUrl: '', // Não usado (background é fixo)
  primaryColor: '#154680',
  cardBackground: '#FFFFFF', // Branco padrão
  faviconUrl: '', // Não usado
  pageTitle: 'Joshua - Sistema de Gestão',
  tenantName: 'Joshua',
  subdomain: ''
};

/**
 * Hook para carregar e processar branding do tenant
 *
 * Detecta automaticamente o tenant pelo subdomínio e carrega
 * as configurações de branding do banco de dados
 *
 * @returns {object} Estado do branding
 * @returns {ProcessedBranding | null} branding - Dados de branding processados ou null enquanto carrega
 * @returns {boolean} loading - Se está carregando
 * @returns {Error | null} error - Erro se houver
 * @returns {boolean} isDefaultBranding - Se está usando branding padrão (sem tenant)
 */
export const useTenantBranding = () => {
  const [branding, setBranding] = useState<ProcessedBranding | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isDefaultBranding, setIsDefaultBranding] = useState<boolean>(false);

  useEffect(() => {
    const loadBranding = async () => {
      try {
        setLoading(true);
        setError(null);

        // Detectar subdomínio
        const subdomain = getTenantSubdomain();

        // Se não tem subdomínio, usar branding padrão
        if (!subdomain) {
          setBranding(DEFAULT_BRANDING);
          setIsDefaultBranding(true);
          return;
        }


        // RPC bypasses tenant RLS (which only allows authenticated users) with
        // a closed-whitelist return surface — safe to call pre-auth.
        const { data, error: fetchError } = await supabase.rpc(
          'fn_get_tenant_branding_by_subdomain',
          { p_subdomain: subdomain }
        );

        if (fetchError) {
          console.error('[useTenantBranding] Erro ao buscar tenant:', fetchError);
          throw fetchError;
        }

        const row = (data as TenantBrandingRow[] | null)?.[0] ?? null;

        if (!row) {
          console.warn(`[useTenantBranding] Tenant com subdomínio '${subdomain}' não encontrado`);
          setBranding(DEFAULT_BRANDING);
          setIsDefaultBranding(true);
          return;
        }


        setBranding(processBranding(row));
        setIsDefaultBranding(false);

      } catch (err) {
        console.error('[useTenantBranding] Erro:', err);
        setError(err as Error);
        // Em caso de erro, usar branding padrão
        setBranding(DEFAULT_BRANDING);
        setIsDefaultBranding(true);
      } finally {
        setLoading(false);
      }
    };

    loadBranding();
  }, []); // Executa apenas uma vez no mount

  return { branding, loading, error, isDefaultBranding };
};

/**
 * Processa linha da RPC em branding utilizável.
 * A RPC já devolve apenas colunas seguras (sem config jsonb bruto).
 */
function processBranding(row: TenantBrandingRow): ProcessedBranding {
  return {
    logoUrl: row.logo_url || '', // Se vazio, não mostra logo no card
    backgroundUrl: '', // Não usado (background fixo)
    primaryColor: row.primary_color || DEFAULT_BRANDING.primaryColor,
    cardBackground: row.card_background || DEFAULT_BRANDING.cardBackground,
    faviconUrl: '', // Não usado (favicon fixo)
    pageTitle: 'Joshua - Sistema de Gestão', // Título fixo
    tenantName: row.nome_fantasia || row.nome,
    subdomain: row.subdomain,
  };
}
